import * as React from 'react'
import { closeEventTypeModal } from '../../store/modals/actions'
import ModalHeader from './Parts/ModalHeader'
import { AppState } from '../../store'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import ModalWindow from './Parts/ModalWindow'
import ModalMiddle from './Parts/ModalMiddle'
import ModalContent from './Parts/ModalContent'
import { useTranslation } from 'react-i18next'
import Button from '../Button/Button'
import { EventTypesController } from '../../controllers'
import { DisplayableError, EventType } from '../../types'
import TooltipError from '../Tooltips/ErrorTooltip'
import Notification from '../../utilities/Notification'
import { sanitizeSlugInput, bookingHost, slugify } from '../Scheduling/slug'
import UrlInput from '../Scheduling/UrlInput'

interface IStateToProps {
  eventType?: Partial<EventType>
  onSubmit?: (eventType: EventType) => void
}

interface IDispatchToProps {
  close: typeof closeEventTypeModal
}

type IProps = IDispatchToProps & IStateToProps

interface IState {
  title: string
  slug: string
  slugTouched: boolean
  duration: string
  errors: DisplayableError[]
  isSubmitting: boolean
}

const EventTypeModal = (props: IProps) => {
  const { t } = useTranslation()
  const { eventType } = props
  const [state, setState] = React.useState<IState>({
    title: eventType?.title || '',
    slug: eventType?.slug || '',
    slugTouched: !!eventType?.id,
    duration: String(eventType?.duration || 30),
    errors: [],
    isSubmitting: false,
  })
  const { title, slug, slugTouched, duration, errors, isSubmitting } = state

  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value
    setState({ ...state, title: value, slug: slugTouched ? slug : sanitizeSlugInput(value) })
  }

  const onSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, slug: sanitizeSlugInput(e.currentTarget.value), slugTouched: true })
  }

  const onSlugBlur = () => {
    setState({ ...state, slug: slugify(slug) })
  }

  const onDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, duration: e.currentTarget.value })
  }

  const onCloseClick = () => {
    props.close()
  }

  const onFormSubmit = async (e) => {
    e.preventDefault()

    if (!title || isSubmitting) return

    const parsedDuration = Number(duration)
    if (!Number.isInteger(parsedDuration) || parsedDuration < 1 || parsedDuration > 1440) {
      setState({ ...state, errors: [{ field: 'duration', message: t('EventTypeModal::Duration must be between 1 and 1440 minutes') }] })
      return
    }

    setState({ ...state, isSubmitting: true })

    try {
      const payload = { title: title, slug: slugify(slug) || slugify(title), duration: parsedDuration }
      const response = eventType?.id
        ? await EventTypesController.update({ ...payload, id: eventType.id })
        : await EventTypesController.create(payload)

      if (response.errors) {
        setState({ ...state, errors: response.errors, isSubmitting: false })
      } else {
        Notification.notifySuccess(eventType?.id
          ? t('EventTypeModal::Event type successfully updated')
          : t('EventTypeModal::Event type successfully created'))
        if (props.onSubmit) props.onSubmit(response)
        props.close()
      }
    } catch (ex) {
      console.error(ex)
      setState({ ...state, isSubmitting: false })
    }
  }

  const onErrorsDismiss = () => {
    setState({ ...state, errors: [] })
  }

  return (
    <ModalWindow>
      <ModalHeader
        title={eventType?.id ? t('EventTypeModal::Update event type') : t('EventTypeModal::New event type')}
        onCloseClick={onCloseClick}
      />

      <ModalMiddle>
        <ModalContent>
          <form onSubmit={onFormSubmit}>
            <div className='grid'>
              <div className='grid-cell with-8col'>
                <div className='form-item'>
                  <label>{t('EventTypeModal::Title')} <span>*</span></label>
                  <input
                    type='text'
                    value={title}
                    onChange={onTitleChange}
                    placeholder={t('EventTypeModal::Quick chat')}
                    autoFocus
                  />
                </div>
              </div>
              <div className='grid-cell with-4col'>
                <div className='form-item'>
                  <label>{t('EventTypeModal::Duration (minutes)')} <span>*</span></label>
                  <input
                    type='number'
                    min={1}
                    max={1440}
                    value={duration}
                    onChange={onDurationChange}
                  />
                </div>
              </div>
              <div className='grid-cell with-12col'>
                <div className='form-item'>
                  <label>{t('EventTypeModal::URL')}</label>
                  <UrlInput>
                    <span>{bookingHost()}/{eventType?.booking_slug ? `${eventType.booking_slug}/` : ''}</span>
                    <input
                      type='text'
                      value={slug}
                      onChange={onSlugChange}
                      onBlur={onSlugBlur}
                      placeholder={t('EventTypeModal::quick-chat')}
                    />
                  </UrlInput>
                </div>
              </div>
            </div>
            <input type='submit' style={{ display: 'none' }} onClick={onFormSubmit} />
          </form>
        </ModalContent>

        <div className='modal-footer'>
          <div />
          <div key='main-action' className='popover-wrapper'>
            <TooltipError
              errors={errors}
              onDismiss={onErrorsDismiss}
            />
            <Button
              type='success'
              text={eventType?.id ? t('EventTypeModal::Update') : t('EventTypeModal::Create')}
              onClick={onFormSubmit}
              disabled={!title}
              isLoading={isSubmitting}
            />
          </div>
        </div>
      </ModalMiddle>
    </ModalWindow>
  )
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    modals: {
      eventTypeModal: {
        eventType,
        onSubmit,
      }
    }
  } = state

  return {
    eventType: eventType,
    onSubmit: onSubmit,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    close: () => dispatch(closeEventTypeModal()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(EventTypeModal)
